// router 實例已內聯於本檔（原 src/router/router.ts 已合併進來）
import App from "@/page/store/index.vue";
import Login from "@/page/login/index.vue";
import Forgot from "@/page/forgot/index.vue";
import ForgotVerification from "@/page/forgot/verification/index.vue";
import ForgotVerificationReset from "@/page/forgot/verification/reset/index.vue";
import Mapping from "@/page/mapping/index.vue";
import Content from "@/page/content/index.vue";
import NotFound from "@/page/404/index.vue";
import { createRouter, createWebHistory } from "vue-router";
import { AdminRouters, GloryRouters, ShepherdRouters } from "@/router/model";
import { RouteNameEnum } from "@/v2/enumerate/route";
import NProgress from "@/config/nprogress";
import Swal from "sweetalert2";

import { usePermission } from "@/hook/usePermission";
import { getSession } from "@/api/module/session";
import { useUser } from "@/hook/useUser";
import { useMitt } from "@/hook/useMitt";
import { useSetting } from "@/hook/useSetting";
import { EnumUserSettingAuthorized } from "@/enum/user";
import { EnumPortalDashboard, EnumPortalName, EnumPortalRouter } from "@/enum/portal";
import { useSwalAlert } from "@/hook/useSwalAlert";
import { useMappingNotice } from "@/hook/useMappingNotice";
import { usePortal } from "@/hook/usePortal";

const { setLoadingMask, setVisibleChangeSelect } = useMitt();

//不管制是否登入頁面
const UnregulatedPage = [
    //登入
    "Login",
    //登入 - (舊版帶index)
    "LoginIndex",
    //忘記密碼
    "Forgot",
    //忘記密碼 - 驗證碼
    "ForgotVerification",
    //忘記密碼 - 驗證碼 - 重設密碼
    "ForgotVerificationReset",
];

//公告頁面的名字
const BillboardPage = ["Billboard", "BillboardEdit"];

const routes = [
    {
        //登入頁面
        path: "/admin/:id",
        name: RouteNameEnum.LOGIN,
        component: Login,
    },
    {
        //登入頁面 (2.0後面有帶個index, 怕用戶有記在書籤裡面)
        path: "/admin/:id/index",
        name: RouteNameEnum.LOGIN_INDEX,
        component: Login,
    },
    {
        //忘記密碼
        path: "/admin/:id/forgot",
        name: RouteNameEnum.FORGOT,
        component: Forgot,
    },
    {
        //忘記密碼 - 驗證碼
        path: "/admin/:id/forgot/verification/:uuid",
        name: RouteNameEnum.FORGOT_VERIFICATION,
        component: ForgotVerification,
    },
    {
        //忘記密碼 - 驗證碼 - 重設密碼
        path: "/admin/:id/forgot/verification/:uuid/reset/:uid",
        name: RouteNameEnum.FORGOT_VERIFICATION_RESET,
        component: ForgotVerificationReset,
    },
    {
        //強迫綁定頁面
        path: "/admin/:id/mapping",
        name: RouteNameEnum.MAPPING,
        component: Mapping,
    },
    {
        //App下載頁面
        path: "/app/:id",
        name: RouteNameEnum.APP,
        component: App,
    },
    {
        //登入後頁面
        path: "/admin/:id",
        name: RouteNameEnum.ADMIN,
        component: Content,
        meta: {
            portal: EnumPortalName.ADMIN,
        },
        children: [...AdminRouters],
    },
    {
        //登入後頁面
        path: "/shepherd/:id",
        name: RouteNameEnum.SHEPHERD,
        component: Content,
        meta: {
            portal: EnumPortalName.SHEPHERD,
        },
        children: [...ShepherdRouters],
    },
    {
        //登入後頁面 - 榮耀堂首頁 (org=4 專屬, 見 beforeEach 的守衛)
        path: "/glory/:id",
        name: RouteNameEnum.GLORY,
        component: Content,
        meta: {
            portal: EnumPortalName.GLORY,
        },
        children: [...GloryRouters],
    },
    {
        // 404頁面
        path: "/:pathMatch(.*)",
        name: RouteNameEnum.NOT_FOUND,
        component: NotFound,
    },
];

/**
 * 創建路由
 * 如果網址後面有個# 請改用 createWebHistory
 */
const router = createRouter({
    history: createWebHistory(),
    routes,
    strict: false,
    // 切換頁面，滾動到最頂部
    scrollBehavior: () => ({ left: 0, top: 0 }),
});

/**
 * 提示 - 尚未綁定App (不強迫跳轉, 僅引導)
 *
 * 每次登入後提示一次 (登入頁打標記, 這裡取出並清除), 換頁 / 重新整理不重複跳;
 * 使用者於對話框勾選「不再顯示此提示」則寫入cookie, 之後重新登入也不再提示。
 *
 * @param uuid 管理員uuid
 * @param id 網址列上的教會no
 */
const noticeMapping = (uuid, id) => {
    const { takeMappingNotice, isMappingNoticeDisabled, disableMappingNotice } = useMappingNotice();

    //取不到管理員uuid不提示
    if (!uuid) return;

    //不是剛登入 (換頁 / 重新整理) 不提示
    if (!takeMappingNotice()) return;

    //已勾選過不再顯示
    if (isMappingNoticeDisabled(uuid)) return;

    //勿在模組頂層呼叫 useSwalAlert()，否則與 useIntent→router 形成循環依賴，HMR 會 TDZ
    useSwalAlert().showMapping(router, id, () => disableMappingNotice(uuid));
};

/**
 * 路由攔截
 */
router.beforeEach(async (to, _, next) => {
    setVisibleChangeSelect(true); //標記要呼叫關閉DatePicker下拉選單
    setLoadingMask(false); //關閉讀取遮罩
    Swal.close(); //移動頁面之前先關閉Swal
    // v2頁面, 將body lock 住
    if (to.meta?.v2 ?? false) {
        document.body.classList.add("v2-lock");
    }
    // 非v2頁面, 執行舊版邏輯
    else {
        // 移除v2的body lock
        document.body.classList.remove("v2-lock");

        //404, 登入頁面, App下載頁面不檢查
        if (!["NotFound", "App"].concat(UnregulatedPage).includes(to.name)) {
            const { isLogin } = usePermission();
            //如果沒有連線權杖, 非登入頁面, 直接強迫跳轉到登入頁面
            //避免使用者提示連線權杖逾時, 按重新整理不會回到登入頁面
            if (!isLogin.value) {
                if (!UnregulatedPage.includes(to.name)) {
                    await router.replace({
                        name: "Login",
                        params: {
                            id: to.params.id,
                        },
                    });
                }
            }
            //權限檢查
            else {
                const { setSettingAuthorized } = useSetting();

                const { setPermission } = usePermission();

                const { getUser, setUser } = useUser();

                const { data } = await getSession();

                // 後端 get() 改以 ok() 回應（{code, data}），成功碼為 200；payload 仍在 data.data
                if (data?.code === 200) {
                    //注入用戶資訊
                    setUser(data.data);
                    //注入權限資料
                    setPermission(data.data.permission);
                    //如果標記已經綁定, 強迫二維碼登入
                    if (data.data.mapping) setSettingAuthorized(EnumUserSettingAuthorized.QRCODE);
                    //教會no
                    const churchNo = to.fullPath.split("/")[2];
                    //路由參數
                    const routeParams = to.params;
                    routeParams.id = getUser.value.church.no;
                    //如果網址列的church.no對不上, 則跳轉至正確位置
                    if (churchNo !== getUser.value.church.no)
                        await router.replace({
                            name: to.name,
                            params: routeParams,
                        });
                    //如果還沒綁管理員, 提示引導前往綁定頁 (不強迫跳轉, 模擬登入 / 綁定頁本身不提示)
                    //另在「個人資訊」頁保留常駐的綁定狀態與前往綁定入口, 使用者可自行前往
                    else if (!getUser.value.mapping && !getUser.value.church.simulation && to.name !== "Mapping")
                        noticeMapping(getUser.value.uuid, routeParams.id);
                }
            }
        }

        //榮耀堂首頁 (org=4 專屬): 沒有權限的人直接打網址進來, 踢回我的教會首頁
        //這是第三層防線 —— 前面還有後端 controller 的 org 硬閘與權限查詢的 org 條件,
        //後面還有 useShow 依 meta.permission 的頁面級檢查
        if (to.meta?.portal === EnumPortalName.GLORY) {
            const { isOrgGlory } = usePortal();
            if (!isOrgGlory.value)
                await router.replace({
                    name: EnumPortalRouter.ADMIN,
                    params: { id: to.params.id },
                });
        }

        //如果是公告+公告頁面
        if (BillboardPage.includes(to.name)) {
            const { isHead } = usePermission();
            //總教會跳轉至首頁
            if (isHead.value)
                await router.replace({
                    name: EnumPortalDashboard[to.meta.portal] ?? EnumPortalRouter.ADMIN,
                    params: { id: to.params.id },
                });
        }
        //控制面板 - 各項統計, 動態修改breadcrumb
        if (to.name === `AdminDashboardStatistics`)
            to.meta.breadcrumb = [
                {
                    key: `admin.home.statistics.${to.params.key}`,
                    value: null,
                },
            ];
    }
    NProgress.start(); // 開始進度條
    return next(); // 開始往下路由
});

router.afterEach(() => {
    loadingEffect(); // 淡出初始化遮罩
    NProgress.done(); // 完成進度條
});

/**
 * 淡出初始化遮罩
 */
const loadingEffect = () =>
    setTimeout(() => {
        //獲取讀取遮罩DOM
        const appLoading = document.getElementById("loading");

        //找不到不往下執行
        if (!appLoading) return;
        //初始化透明度
        let opacity = 1;
        //如果本來的讀取遮罩DOM的style沒有透明度, 先補上去
        if (!appLoading.style.opacity) {
            appLoading.style.opacity = String(opacity);
        }
        //淡出方法
        const fadeEffect = setInterval(() => {
            //如果還沒透明到底
            if (opacity > 0) {
                appLoading.style.opacity = String(opacity);
            }
            //透明到底
            else {
                //移除讀取遮罩
                appLoading.remove();
                //移除body上的初始化標記
                document.getElementsByTagName("body")[0].classList.remove("initial");
                //清除淡出方法週期
                clearInterval(fadeEffect);
            }
            //遞減透明度
            opacity -= 0.1;
        }, 20);
    }, 50);

export default router;
